import { useState, useEffect, useCallback } from 'react';

import Card from '@/utils/card';
import { CARD_ERROR_MAP } from '@/components/Cards/config';

import { IdCardInfo, IdCardHook } from './interface';

const useIdCard = (): IdCardHook => {
  const defaultIdCardInfo: IdCardInfo = {
    id: '', // 证件号
    name: '', // 姓名
    address: '', // 住址
    nation: '', // 民族
    sex: '', // 性别
    type: '', // 证件类型
    birthdate: '', // 出生日期
    effecteddate: '', // 有效期开始
    expireddate: '', // 有效期结束
    agency: '', // 签发机关
  };

  const [cardInstance, setCardInstance] = useState(null);
  const [rafId, setRafId] = useState(0);
  const [idCardInfo, setIdCardInfo] = useState({ ...defaultIdCardInfo });
  const [readError, setReadError] = useState(false);

  let start;

  // poll read
  const readCard = (timestamp) => {
    if (start === undefined) {
      start = timestamp;
    }

    // setIdCardInfo({ ...defaultIdCardInfo });
    const elapsed = timestamp - start;

    // 500ms gap
    if (elapsed > 500 && cardInstance !== null && cardInstance.connected) {
      cardInstance
        .readIdCard()
        .then((data) => {
          setIdCardInfo(data);
          // clear loop
          if (rafId) cancelAnimationFrame(rafId);
        })
        .catch((e) => {
          // 未放卡或卡片未放好
          if (e.code === CARD_ERROR_MAP.noCard) {
            // go on read
            setReadError(false);
            start = undefined;
            setRafId(requestAnimationFrame(readCard));
          } else {
            setReadError(true);
            // message.error(e.error || '读卡失败');
            // clear loop
            if (rafId) cancelAnimationFrame(rafId);
          }
        });
    } else {
      setRafId(requestAnimationFrame(readCard));
    }
  };

  // reset current states
  const resetCardStates = () => {
    if (cardInstance !== null && cardInstance.connected) cardInstance.close();
    setCardInstance(null);
    setReadError(false);
    setIdCardInfo({ ...defaultIdCardInfo });
    if (rafId) cancelAnimationFrame(rafId);
  };

  const startRead = useCallback(() => {
    // init ws connect
    const c = new Card();
    setCardInstance(c);
  }, []);

  // retry
  const retryRead = useCallback(() => {
    if (cardInstance !== null) {
      start = undefined;
      setRafId(requestAnimationFrame(readCard));
    }
  }, []);

  // init read
  useEffect(() => {
    if (cardInstance !== null) {
      setRafId(requestAnimationFrame(readCard));
    }
  }, [cardInstance]);

  // reset states when unmounted
  useEffect(() => {
    return () => {
      resetCardStates();
    };
  }, []);

  return {
    cardInstance,
    idCardInfo,
    readError,
    resetCardStates,
    startRead,
    retryRead,
  };
};

export default useIdCard;
